// @flow
import { formatLocale, precisionFixed } from 'd3-format'
import { geoIdentity, geoPath } from 'd3-geo'
import * as topojson from 'topojson-client'
import { map } from 'd3-collection'
import { scaleQuantize } from 'd3-scale'
import { interpolateRgbBasis } from 'd3-interpolate'
// Settings configured here will be merged into the final config object.

const deChLocale = formatLocale(require('de-ch-locale'))
const precision = precisionFixed(0.01)
const formatAsDecimal: number => string = deChLocale.format(
  ',.' + precision + 'f'
)
const formatAsCurrency: number => string = deChLocale.format('$,.2f')
const formatAsIntegerCurrency: number => string = deChLocale.format('$,')
const formatAsPercent: number => string = deChLocale.format('.1%')
/**
 * Formats a number as an integer
 * @param {number}
 * @returns {string}
 */
const formatAsInteger: number => string = deChLocale.format(',d')

const apiEndpoint = 'https://steuerbelastungskarte.firebaseio.com'

// region GeoJSON data
const schweiz = require('../components/map/switzerland.json')
const kantone = schweiz.objects.kantone
const seen = schweiz.objects.seen
const gemeindegrenzen = schweiz.objects.gemeinden
const zuercherKreise = schweiz.objects.zuerichKreise

const gemeindenUndKreise = {
  type: 'GeometryCollection',
  geometries: schweiz.objects.gemeinden.geometries.concat(
    schweiz.objects.zuerichKreise.geometries
  )
}

const mapWidth = 1000
const mapHeight = 630
// endregion

// region Price and Size Ranges
const flatSizeCategories = [
  ['1', '0,5 bis 1,5 Zimmer', '0,5 bis 1,5 Zimmern'],
  ['2', '2 bis 2,5 Zimmer', '2 bis 2,5 Zimmern'],
  ['3', '3 bis 3,5 Zimmer', '3 bis 3,5 Zimmern'],
  ['4', '4 bis 4,5 Zimmer', '4 bis 4,5 Zimmern'],
  ['5', '5 oder mehr Zimmer', '5 oder mehr Zimmern']
]

const budgetCategories = [
  ['1', 'bis CHF 500'],
  ['2', 'bis CHF 1000'],
  ['3', 'bis CHF 1500'],
  ['4', 'bis CHF 2000'],
  ['5', 'bis CHF 2500'],
  ['6', 'bis CHF 3000']
]

const priceRangeBrackets = [
  '< CHF 500',
  'CHF 501–1000',
  'CHF 1001–1500',
  'CHF 1501–2000',
  'CHF 2001–2500',
  'CHF 2501–3000',
  '> CHF 3000'
]

// endregion

// region Geo JSON functions
const pathGenerator: any => string = geoPath().projection(
  geoIdentity().fitSize(
    [mapWidth, mapHeight],
    topojson.feature(schweiz, kantone)
  )
)
// endregion

// region Indexed Municipality Features
const features = topojson.feature(schweiz, gemeindegrenzen).features
/**
 * An indexed list of municipality borders, indexed by municipality ID
 */
const indexedMunicipalityFeatures = map(
  features,
  feature => feature.properties['GMDNR']
)

const kreisFeatures = topojson.feature(schweiz, zuercherKreise).features

const indexedZurichKreise = map(
  kreisFeatures,
  feature => feature.properties['knr']
)

// endregion

// region Colour Generator

const tickNumber = 5

/**
 * Base colours
 * @type {string[]}
 */
const colors = ['#276419', '#7FBC41', '#B8E186', '#C51B7D', '#8E0152']

/**
 * Create a linear colour scale
 */
const linearColourScale: (input: number) => string = interpolateRgbBasis(colors)

/**
 * Create more colours from the original colours
 * @param amountOfColors
 */
const moreColors: number => Array<string> = amountOfColors =>
  [...Array(amountOfColors).keys()].map(i =>
    linearColourScale(i / amountOfColors)
  )

const quantizedColourScale: (input: number) => string = scaleQuantize()
  .domain([0, 1])
  .range(moreColors(8))

/**
 * Generates a colour for maps
 * @param input
 */
const colorGenerator = (input: number) => {
  return linearColourScale(input)
}

const ticks = quantizedColourScale.ticks(tickNumber)

// endregion

const notEnoughData = 80

export default {
  apiEndpoint,
  colorGenerator,
  colorScale: ['#8E0152', '#EEEEEE', '#276419'],
  flatSizeCategories,
  formatAsCurrency,
  formatAsDecimal,
  formatAsInteger,
  formatAsIntegerCurrency,
  formatAsPercent,
  gemeindegrenzen,
  gemeindenUndKreise,
  indexedMunicipalityFeatures,
  indexedZurichKreise,
  invalidColor: '#CDD2D3',
  kantone,
  locale: deChLocale,
  mapHeight,
  mapWidth,
  notEnoughData,
  pathGenerator,
  budgetCategories: budgetCategories,
  priceRangeBrackets,
  quantizedColourScale,
  schweiz,
  screenUnit: mapWidth / (window.innerWidth - 26),
  seen,
  ticks,
  zuercherKreise
}



// WEBPACK FOOTER //
// ./src/config/base.js